/*───────────────────────────────────────────────────────────────
 📄 File: themeColorUtils.ts
 🧩 Description:
    Pure color utility functions for theme management.
    Extracted from store/theme.ts.
────────────────────────────────────────────────────────────────*/

import { ThemeMode, LightModeVariant, DarkModeVariant } from './theme';

/** Convert a hex color string to HSL format string (e.g. "210 40% 98%") */
export function hexToHsl(hex: string): string {
    const hexColor = hex.replace('#', '');
    const r = parseInt(hexColor.substring(0, 2), 16) / 255;
    const g = parseInt(hexColor.substring(2, 4), 16) / 255;
    const b = parseInt(hexColor.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Return white or black depending on the luminance of the given hex background color */
export function getContrastColor(hexColor: string): string {
    const color = hexColor.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
}

/** Convert a hex color string to comma-separated RGB values (e.g. "34, 197, 94") for use in rgba() */
export function hexToRgbComma(hex: string): string {
    const hexColor = hex.replace('#', '');
    if (hexColor.length !== 6 && hexColor.length !== 3) return '0, 0, 0';
    
    let r = 0, g = 0, b = 0;
    if (hexColor.length === 3) {
        r = parseInt(hexColor[0] + hexColor[0], 16);
        g = parseInt(hexColor[1] + hexColor[1], 16);
        b = parseInt(hexColor[2] + hexColor[2], 16);
    } else {
        r = parseInt(hexColor.substring(0, 2), 16);
        g = parseInt(hexColor.substring(2, 4), 16);
        b = parseInt(hexColor.substring(4, 6), 16);
    }
    return `${r}, ${g}, ${b}`;
}

/** Return the appropriate input background color for a given theme variant */
export function getInputBackgroundColor(
    themeMode: ThemeMode,
    lightVariant?: LightModeVariant,
    darkVariant?: DarkModeVariant,
): string {
    if (themeMode === 'dark') {
        const DARK_MAP: Record<DarkModeVariant, string> = {
            mint: '#0f172a',
            navy: '#1e293b',
            mirage: '#0f1419',
            cinder: '#1c1c1c',
            black: '#000000',
            'dark-gray': '#1E293B',
            'pure-black': '#141414',
            'very-dark-gray': '#1A1A1A',
        };
        return DARK_MAP[darkVariant as DarkModeVariant] ?? '#1E293B';
    }
    const LIGHT_MAP: Record<LightModeVariant, string> = {
        slate: '#f8fafc',
        gray: '#f9fafb',
        neutral: '#fafafa',
        white: '#E2E8F0',
        'very-light-green': '#D4E6D4',
        'very-light-purple': '#D4D4E6',
        'light-green': '#D6E4DD',
    };
    return LIGHT_MAP[lightVariant as LightModeVariant] ?? '#E2E8F0';
}
